import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  serviceProviderService,
  SpUserPrefillDto,
} from '../../services/serviceProvider.service';
import { contentService, IsdCodeValuesDto } from '../../services/content.service';
import { PremiumSelect } from '../../components/PremiumSelect/PremiumSelect';
import './ServiceProvider.scss';
import { CompactHeader } from '../../components/CompactHeader/CompactHeader';
import { CompactFooter } from '../../components/CompactFooter/CompactFooter';
import { RegistrationStepper } from '../../components/RegistrationStepper/RegistrationStepper';
import { RegistrationVisualCard } from '../../components/RegistrationVisualCard/RegistrationVisualCard';

/**
 * Pixel-faithful port of `service-provider/services_provider_user_register.blade.php`.
 *
 * Entry: /service-provider/user-register?serviceProviderEmail=<encrypted>
 * Submit POST → /api/clients/sp/user-register
 *   → on success, navigate to /service-provider/thank-you
 *   (Laravel: `redirect()->route('service_provider_register_thank_you_show')`).
 */
const SpUserRegister: React.FC = () => {
  const [params] = useSearchParams();
  const spEmail = params.get('serviceProviderEmail') ?? '';
  const navigate = useNavigate();

  const [prefill, setPrefill] = useState<SpUserPrefillDto | null>(null);
  // The agent's number is shown to investors on the delegation card when they decide whether
  // to grant access, so it needs a country code like every other number we display.
  const [isdCodes, setIsdCodes] = useState<IsdCodeValuesDto[]>([]);
  const [isdCodeId, setIsdCodeId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [designation, setDesignation] = useState('');
  const [officialEmail, setOfficialEmail] = useState('');
  const [officialPhone, setOfficialPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Best-effort: a failed lookup must not block registration, it only leaves the
    // country-code select empty.
    contentService
      .getIsdCodes()
      .then(setIsdCodes)
      .catch(() => setIsdCodes([]));
  }, []);

  useEffect(() => {
    if (!spEmail) {
      setError('Invalid service provider link.');
      return;
    }
    serviceProviderService
      .getUserPrefill(spEmail)
      .then((p) => {
        setPrefill(p);
        setFirstName(p.complianceName || '');
        setLastName(p.complianceLastName || '');
        setOfficialEmail(p.complianceEmail && p.complianceEmail !== 'NA' ? p.complianceEmail : '');
        setOfficialPhone(
          p.compliancePhoneNo && p.compliancePhoneNo !== 'NA' ? p.compliancePhoneNo : '',
        );
      })
      .catch(() => setError('Invalid service provider link.'));
  }, [spEmail]);

  const validate = (): string | null => {
    if (!firstName.trim() || !lastName.trim() || !designation.trim() ||
        !officialEmail.trim() || !officialPhone.trim() || !consent) {
      return 'Please fill all required fields and accept consent.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(officialEmail.trim())) {
      return 'Please enter a valid email';
    }
    if (!isdCodeId) {
      return 'Please select the country code for the phone number';
    }
    const phone = officialPhone.trim();
    if (!/^\d+$/.test(phone)) {
      return 'Phone number must contain digits only';
    }
    if (phone.startsWith('0')) {
      return 'Enter the phone number without the leading 0';
    }
    if (phone.length < 10 || phone.length > 15) {
      return 'Phone number must be between 10 and 15 digits';
    }
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const v = validate();
    if (v) {
      setSubmitError(v);
      return;
    }
    if (!prefill) return;

    setSubmitting(true);
    try {
      const resp = await serviceProviderService.registerUser({
        serviceProviderEmail: spEmail,
        serviceProviderNameHidden: prefill.fullName,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        designation: designation.trim() || undefined,
        officialEmail: officialEmail.trim(),
        // Prefixed with the dial code: service_agents.mobile is a single column and the
        // delegation card renders it verbatim.
        officialPhone: `+${isdCodes.find((i) => String(i.id) === isdCodeId)?.codeValue ?? ''}${officialPhone.trim()}`,
        consent,
      });
      if (resp.success) {
        // Carried in router state, not the URL — same as the investor success screen, so a
        // refresh drops it and the screen falls back to "Go to Login".
        navigate('/service-provider/thank-you', {
          state: { azureUserId: resp.azureUserId, loginStatus: resp.loginStatus, b2bInviteSent: resp.b2bInviteSent },
        });
      } else {
        setSubmitError(resp.message || 'Could not register. Please try again.');
        setSubmitting(false);
      }
    } catch {
      setSubmitError('Could not register. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <>
      <CompactHeader />
      <section className="login-form-style4 steps4-sec section-padding align-items-center">
        <div className="container">
          <div className="registration-split-layout">
            {/* Left Panel: Form & Stepper */}
            <div className="left-panel">
              {/* Stepper Progress */}
              <RegistrationStepper currentStep={5} title="Registration Details" maxWidth="550px" />

              {/* Card */}
              <div className="login-form-style3-main" style={{ width: '100%', maxWidth: '550px', margin: '0 auto', textAlign: 'left' }}>
                <div className="login-form-style3-main_full">
                  <div className="login-register3-form-middle">
                    {error && <div style={{ color: '#be1717', marginBottom: 12 }}>{error}</div>}
                    {submitError && <div style={{ color: '#be1717', marginBottom: 12 }}>{submitError}</div>}

                    <form onSubmit={onSubmit}>
                      <div className="single-field full-row">
                        <label>
                          Service Provider Name <span className="star-color">*</span>
                        </label>
                        <input type="text" value={prefill?.fullName ?? ''} readOnly style={{ textTransform: 'uppercase' }} />
                      </div>

                      <div className="single-field">
                        <label>
                          First Name <span className="star-color">*</span>
                        </label>
                        <input
                          type="text"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                          readOnly={!!prefill?.complianceName}
                          style={prefill?.complianceName ? { textTransform: 'uppercase' } : undefined}
                        />
                      </div>

                      <div className="single-field">
                        <label>
                          Last Name <span className="star-color">*</span>
                        </label>
                        <input
                          type="text"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          required
                          readOnly={!!prefill?.complianceLastName}
                          style={prefill?.complianceLastName ? { textTransform: 'uppercase' } : undefined}
                        />
                      </div>

                      <div className="single-field">
                        <label>
                          Designation <span className="star-color">*</span>
                        </label>
                        <input
                          type="text"
                          value={designation}
                          onChange={(e) => setDesignation(e.target.value.toUpperCase())}
                          required
                          style={{ textTransform: 'uppercase' }}
                        />
                      </div>

                      <div className="single-field">
                        <label>
                          Official Email <span className="star-color">*</span>
                        </label>
                        <input
                          type="email"
                          value={officialEmail}
                          onChange={(e) => setOfficialEmail(e.target.value)}
                          required
                          readOnly={!!(prefill?.complianceEmail && prefill.complianceEmail !== 'NA')}
                          style={prefill?.complianceEmail && prefill.complianceEmail !== 'NA' ? { textTransform: 'uppercase' } : undefined}
                        />
                      </div>

                      <div className="single-field">
                        <label>
                          Country Code <span className="star-color">*</span>
                        </label>
                        <PremiumSelect
                          value={isdCodeId}
                          onChange={setIsdCodeId}
                          options={isdCodes.map((i) => ({
                            value: String(i.id),
                            label: `+${i.codeValue} (${i.countryName})`,
                            selectedLabel: `+${i.codeValue}`,
                          }))}
                          placeholder="Select Code"
                          searchable
                        />
                      </div>

                      <div className="single-field">
                        <label>
                          Official Phone <span className="star-color">*</span>
                        </label>
                        <input
                          type="tel"
                          value={officialPhone}
                          onChange={(e) => setOfficialPhone(e.target.value.replaceAll(/\D/g, ''))}
                          required
                          minLength={10}
                          maxLength={15}
                        />
                      </div>

                      <div className="full-row highlight">
                        <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', margin: 0 }}>
                          <input
                            type="checkbox"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            required
                            className="w-4 h-4 text-[#be1717] bg-white border-slate-300 rounded focus:ring-[#be1717] cursor-pointer align-middle"
                            style={{ marginRight: '0.5rem', marginTop: '0.2rem', flexShrink: 0 }}
                          />
                          <span style={{ fontSize: '13px', color: '#334155', fontWeight: 500 }}>
                            I/We confirm that I/We have read and agree to the Service Provider User
                            Privacy &amp; Consent Notice
                          </span>
                        </label>
                        <p className="consent-note" style={{ paddingLeft: '1.7rem', margin: '4px 0 0', fontSize: '11px', color: '#64748b' }}>
                          Consent is mandatory for registration and platform access.
                        </p>
                      </div>

                      <div className="actions mb-0 text-center border-t pt-3 mt-3 flex justify-center gap-2">
                        <button
                          className="button-2"
                          type="button"
                          onClick={() => window.history.back()}
                          disabled={submitting}
                        >
                          Cancel
                        </button>
                        <button className="button-1" type="submit" disabled={submitting}>
                          {submitting ? 'Submitting...' : 'Submit Registration'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Creative Illustration Panel */}
            <div className="right-panel">
              <RegistrationVisualCard step="details" />
            </div>
          </div>
        </div>
      </section>
      <CompactFooter />
    </>
  );
};

export default SpUserRegister;
